(() => {
  'use strict';

  const VERSION = '1.0.2';
  const STORAGE_KEY = 'bb_a11y_state_v1';
  const HOST_ID = 'bb-a11y-host';
  const ROOT = document.documentElement;
  let baseRootFontSize = 16;
  let lastOpener = null;
  let speech = null;
  let host = null;
  let shadow = null;
  let panel = null;
  let state = null;
  let config = null;

  const PROFILE_PRESETS = {
    motor: { keyboardNav: true, focusHighlight: true, largeTargets: true, largeCursor: true },
    vision: { fontScale: 1.2, contrast: 'high', textMagnifier: true, highlightLinks: true, focusHighlight: true },
    epilepsy: { stopAnimations: true, saturation: 0.8, brightness: 0.95 },
    cognitive: { readableFont: true, lineSpacing: 1.7, letterSpacing: 0.03, readingGuide: true, highlightLinks: true, simplify: true },
    adhd: { stopAnimations: true, readingGuide: true, readingMask: true, simplify: true },
    colorblind: { saturation: 1.35, highlightLinks: true, colorVision: 'deuteranopia' }
  };

  const DEFAULT_MANUAL = {
    fontScale: 1,
    alignment: 'default',
    lineSpacing: 0,
    letterSpacing: 0,
    readableFont: false,
    contrast: 'normal',
    saturation: 1,
    brightness: 1,
    grayscale: false,
    invertColors: false,
    colorVision: 'none',
    highlightLinks: false,
    focusHighlight: false,
    largeTargets: false,
    keyboardNav: false,
    hideImages: false,
    stopAnimations: false,
    readingGuide: false,
    readingMask: false,
    textMagnifier: false,
    largeCursor: false,
    simplify: false
  };

  const I18N = {
    en: {
      title: 'Accessibility', close: 'Close', reset: 'Reset', language: 'Language', profiles: 'Accessibility profiles', visual: 'Visual settings', content: 'Content settings', reading: 'Reading tools',
      motor: 'Motor', vision: 'Vision', epilepsy: 'Epilepsy', cognitive: 'Cognitive', adhd: 'ADHD', colorblind: 'Color blindness',
      textSize: 'Text size', smaller: 'Smaller', larger: 'Larger', alignment: 'Text alignment', left: 'Left', center: 'Center', right: 'Right', default: 'Default',
      lineSpacing: 'Line spacing', letterSpacing: 'Letter spacing', readableFont: 'Readable font', magnifier: 'Text magnifier',
      highContrast: 'High contrast', lowContrast: 'Low contrast', darkMode: 'Dark mode', grayscale: 'Grayscale', invert: 'Invert colors', saturationUp: 'More saturation', saturationDown: 'Less saturation', brightnessUp: 'More brightness', brightnessDown: 'Less brightness', colorVision: 'Color filter',
      links: 'Highlight links', focus: 'Focus highlight', targets: 'Large click targets', keyboard: 'Keyboard navigation', hideImages: 'Hide images', stopAnimations: 'Stop animations',
      readingGuide: 'Reading guide', readingMask: 'Reading mask', largeCursor: 'Large cursor', simplify: 'Reduce distractions', readerView: 'Reader view', pageStructure: 'Page structure', speakSelection: 'Read selected text', stopSpeech: 'Stop speech',
      statement: 'Accessibility statement', feedback: 'Accessibility feedback', active: 'Active', normal: 'Normal', size: 'Panel size', small: 'Small', medium: 'Medium', large: 'Large',
      outlineTitle: 'Page structure', readerTitle: 'Reader view', noSelection: 'Select text on the page first, or focus an element to read it aloud.', noContent: 'No readable main content was found.', closeView: 'Close view'
    },
    fr: {
      title: 'Accessibilité', close: 'Fermer', reset: 'Réinitialiser', language: 'Langue', profiles: 'Profils d’accessibilité', visual: 'Réglages visuels', content: 'Réglages du contenu', reading: 'Outils de lecture',
      motor: 'Moteur', vision: 'Vision', epilepsy: 'Épilepsie', cognitive: 'Cognitif', adhd: 'TDAH', colorblind: 'Daltonisme', textSize: 'Taille du texte', smaller: 'Réduire', larger: 'Agrandir', alignment: 'Alignement', left: 'Gauche', center: 'Centre', right: 'Droite', default: 'Défaut', lineSpacing: 'Interligne', letterSpacing: 'Espacement des lettres', readableFont: 'Police lisible', magnifier: 'Loupe de texte', highContrast: 'Contraste élevé', lowContrast: 'Contraste réduit', darkMode: 'Mode sombre', grayscale: 'Niveaux de gris', invert: 'Inverser les couleurs', saturationUp: 'Plus de saturation', saturationDown: 'Moins de saturation', brightnessUp: 'Plus de luminosité', brightnessDown: 'Moins de luminosité', colorVision: 'Filtre de couleur', links: 'Surligner les liens', focus: 'Surligner le focus', targets: 'Grandes zones cliquables', keyboard: 'Navigation clavier', hideImages: 'Masquer les images', stopAnimations: 'Arrêter les animations', readingGuide: 'Guide de lecture', readingMask: 'Masque de lecture', largeCursor: 'Grand curseur', simplify: 'Réduire les distractions', readerView: 'Mode lecture', pageStructure: 'Structure de la page', speakSelection: 'Lire le texte sélectionné', stopSpeech: 'Arrêter la lecture', statement: 'Déclaration d’accessibilité', feedback: 'Retour accessibilité', active: 'Actif', normal: 'Normal', size: 'Taille du panneau', small: 'Petit', medium: 'Moyen', large: 'Grand', outlineTitle: 'Structure de la page', readerTitle: 'Mode lecture', noSelection: 'Sélectionnez du texte ou placez le focus sur un élément à lire.', noContent: 'Aucun contenu principal lisible trouvé.', closeView: 'Fermer'
    },
    de: {
      title: 'Barrierefreiheit', close: 'Schließen', reset: 'Zurücksetzen', language: 'Sprache', profiles: 'Barrierefreiheitsprofile', visual: 'Visuelle Einstellungen', content: 'Inhaltseinstellungen', reading: 'Lesehilfen', motor: 'Motorik', vision: 'Sehen', epilepsy: 'Epilepsie', cognitive: 'Kognitiv', adhd: 'ADHS', colorblind: 'Farbenblindheit', textSize: 'Textgröße', smaller: 'Kleiner', larger: 'Größer', alignment: 'Textausrichtung', left: 'Links', center: 'Mitte', right: 'Rechts', default: 'Standard', lineSpacing: 'Zeilenabstand', letterSpacing: 'Buchstabenabstand', readableFont: 'Lesbare Schrift', magnifier: 'Textlupe', highContrast: 'Hoher Kontrast', lowContrast: 'Niedriger Kontrast', darkMode: 'Dunkelmodus', grayscale: 'Graustufen', invert: 'Farben umkehren', saturationUp: 'Mehr Sättigung', saturationDown: 'Weniger Sättigung', brightnessUp: 'Mehr Helligkeit', brightnessDown: 'Weniger Helligkeit', colorVision: 'Farbfilter', links: 'Links hervorheben', focus: 'Fokus hervorheben', targets: 'Große Klickflächen', keyboard: 'Tastaturnavigation', hideImages: 'Bilder ausblenden', stopAnimations: 'Animationen stoppen', readingGuide: 'Lesehilfe', readingMask: 'Lesemaske', largeCursor: 'Großer Cursor', simplify: 'Ablenkungen reduzieren', readerView: 'Leseansicht', pageStructure: 'Seitenstruktur', speakSelection: 'Auswahl vorlesen', stopSpeech: 'Vorlesen stoppen', statement: 'Erklärung zur Barrierefreiheit', feedback: 'Feedback zur Barrierefreiheit', active: 'Aktiv', normal: 'Normal', size: 'Panelgröße', small: 'Klein', medium: 'Mittel', large: 'Groß', outlineTitle: 'Seitenstruktur', readerTitle: 'Leseansicht', noSelection: 'Wählen Sie Text aus oder fokussieren Sie ein Element zum Vorlesen.', noContent: 'Kein lesbarer Hauptinhalt gefunden.', closeView: 'Schließen'
    },
    it: {
      title: 'Accessibilità', close: 'Chiudi', reset: 'Reimposta', language: 'Lingua', profiles: 'Profili di accessibilità', visual: 'Impostazioni visive', content: 'Impostazioni contenuto', reading: 'Strumenti di lettura', motor: 'Motorio', vision: 'Vista', epilepsy: 'Epilessia', cognitive: 'Cognitivo', adhd: 'ADHD', colorblind: 'Daltonismo', textSize: 'Dimensione testo', smaller: 'Riduci', larger: 'Aumenta', alignment: 'Allineamento testo', left: 'Sinistra', center: 'Centro', right: 'Destra', default: 'Predefinito', lineSpacing: 'Interlinea', letterSpacing: 'Spaziatura lettere', readableFont: 'Carattere leggibile', magnifier: 'Lente testo', highContrast: 'Alto contrasto', lowContrast: 'Basso contrasto', darkMode: 'Modalità scura', grayscale: 'Scala di grigi', invert: 'Inverti colori', saturationUp: 'Più saturazione', saturationDown: 'Meno saturazione', brightnessUp: 'Più luminosità', brightnessDown: 'Meno luminosità', colorVision: 'Filtro colore', links: 'Evidenzia link', focus: 'Evidenzia focus', targets: 'Aree clic grandi', keyboard: 'Navigazione tastiera', hideImages: 'Nascondi immagini', stopAnimations: 'Ferma animazioni', readingGuide: 'Guida lettura', readingMask: 'Maschera lettura', largeCursor: 'Cursore grande', simplify: 'Riduci distrazioni', readerView: 'Vista lettura', pageStructure: 'Struttura pagina', speakSelection: 'Leggi testo selezionato', stopSpeech: 'Ferma lettura', statement: 'Dichiarazione di accessibilità', feedback: 'Feedback accessibilità', active: 'Attivo', normal: 'Normale', size: 'Dimensione pannello', small: 'Piccolo', medium: 'Medio', large: 'Grande', outlineTitle: 'Struttura pagina', readerTitle: 'Vista lettura', noSelection: 'Seleziona del testo o porta il focus su un elemento.', noContent: 'Nessun contenuto principale leggibile trovato.', closeView: 'Chiudi'
    },
    es: {
      title: 'Accesibilidad', close: 'Cerrar', reset: 'Restablecer', language: 'Idioma', profiles: 'Perfiles de accesibilidad', visual: 'Ajustes visuales', content: 'Ajustes de contenido', reading: 'Herramientas de lectura', motor: 'Motricidad', vision: 'Visión', epilepsy: 'Epilepsia', cognitive: 'Cognitivo', adhd: 'TDAH', colorblind: 'Daltonismo', textSize: 'Tamaño del texto', smaller: 'Reducir', larger: 'Aumentar', alignment: 'Alineación', left: 'Izquierda', center: 'Centro', right: 'Derecha', default: 'Predeterminado', lineSpacing: 'Interlineado', letterSpacing: 'Espaciado de letras', readableFont: 'Fuente legible', magnifier: 'Lupa de texto', highContrast: 'Alto contraste', lowContrast: 'Bajo contraste', darkMode: 'Modo oscuro', grayscale: 'Escala de grises', invert: 'Invertir colores', saturationUp: 'Más saturación', saturationDown: 'Menos saturación', brightnessUp: 'Más brillo', brightnessDown: 'Menos brillo', colorVision: 'Filtro de color', links: 'Resaltar enlaces', focus: 'Resaltar foco', targets: 'Áreas de clic grandes', keyboard: 'Navegación por teclado', hideImages: 'Ocultar imágenes', stopAnimations: 'Detener animaciones', readingGuide: 'Guía de lectura', readingMask: 'Máscara de lectura', largeCursor: 'Cursor grande', simplify: 'Reducir distracciones', readerView: 'Vista de lectura', pageStructure: 'Estructura de página', speakSelection: 'Leer texto seleccionado', stopSpeech: 'Detener lectura', statement: 'Declaración de accesibilidad', feedback: 'Comentarios de accesibilidad', active: 'Activo', normal: 'Normal', size: 'Tamaño del panel', small: 'Pequeño', medium: 'Medio', large: 'Grande', outlineTitle: 'Estructura de página', readerTitle: 'Vista de lectura', noSelection: 'Selecciona texto o enfoca un elemento para leerlo.', noContent: 'No se encontró contenido principal legible.', closeView: 'Cerrar'
    },
    ja: {
      title: 'アクセシビリティ', close: '閉じる', reset: 'リセット', language: '言語', profiles: 'アクセシビリティプロファイル', visual: '表示設定', content: 'コンテンツ設定', reading: '読み上げ・読書ツール', motor: '運動機能', vision: '視覚', epilepsy: 'てんかん', cognitive: '認知', adhd: 'ADHD', colorblind: '色覚', textSize: '文字サイズ', smaller: '小さく', larger: '大きく', alignment: '文字揃え', left: '左', center: '中央', right: '右', default: '標準', lineSpacing: '行間', letterSpacing: '文字間隔', readableFont: '読みやすいフォント', magnifier: '文字拡大', highContrast: '高コントラスト', lowContrast: '低コントラスト', darkMode: 'ダークモード', grayscale: 'グレースケール', invert: '色を反転', saturationUp: '彩度を上げる', saturationDown: '彩度を下げる', brightnessUp: '明るく', brightnessDown: '暗く', colorVision: '色覚フィルター', links: 'リンクを強調', focus: 'フォーカス強調', targets: 'クリック領域を拡大', keyboard: 'キーボード操作', hideImages: '画像を隠す', stopAnimations: 'アニメーション停止', readingGuide: 'リーディングガイド', readingMask: 'リーディングマスク', largeCursor: '大きなカーソル', simplify: '気を散らす要素を減らす', readerView: 'リーダービュー', pageStructure: 'ページ構造', speakSelection: '選択テキストを読み上げ', stopSpeech: '読み上げ停止', statement: 'アクセシビリティ声明', feedback: 'アクセシビリティのご意見', active: '有効', normal: '標準', size: 'パネルサイズ', small: '小', medium: '中', large: '大', outlineTitle: 'ページ構造', readerTitle: 'リーダービュー', noSelection: '読み上げるテキストを選択するか、要素にフォーカスしてください。', noContent: '読み取れるメインコンテンツが見つかりません。', closeView: '閉じる'
    }
  };

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]));
  }

  function getConfig() {
    const el = document.querySelector('[data-bb-a11y-config]');
    const d = el ? el.dataset : {};
    return {
      triggerMode: d.triggerMode || 'link',
      position: d.position || 'left',
      accent: d.accent || '#111111',
      buttonLabel: d.buttonLabel || 'Accessibility',
      statementUrl: d.statementUrl || '/pages/accessibility-statement',
      supportEmail: d.supportEmail || '',
      defaultLanguage: d.defaultLanguage || 'auto',
      showProfiles: d.showProfiles !== 'false',
      showStatement: d.showStatement !== 'false'
    };
  }

  function initialLanguage() {
    if (config.defaultLanguage !== 'auto' && I18N[config.defaultLanguage]) return config.defaultLanguage;
    const lang = (navigator.language || 'en').slice(0,2).toLowerCase();
    return I18N[lang] ? lang : 'en';
  }

  function loadState() {
    let saved = {};
    try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}'); } catch (_) {}
    return {
      language: saved.language || initialLanguage(),
      panelSize: saved.panelSize || 'medium',
      profiles: { motor:false, vision:false, epilepsy:false, cognitive:false, adhd:false, colorblind:false, ...(saved.profiles || {}) },
      manual: { ...DEFAULT_MANUAL, ...(saved.manual || {}) }
    };
  }

  function saveState() {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
  }

  function t(key) {
    return I18N[state.language]?.[key] || I18N.en[key] || key;
  }

  function effectiveState() {
    const out = { ...DEFAULT_MANUAL, ...state.manual };
    Object.entries(state.profiles).forEach(([name, enabled]) => {
      if (!enabled) return;
      const preset = PROFILE_PRESETS[name];
      if (!preset) return;
      Object.entries(preset).forEach(([key, value]) => {
        if (typeof value === 'number') {
          if (key === 'fontScale' || key === 'saturation' || key === 'brightness' || key === 'lineSpacing' || key === 'letterSpacing') out[key] = Math.max(out[key] || 0, value);
          else out[key] = value;
        } else if (typeof value === 'boolean') out[key] = out[key] || value;
        else if (out[key] === DEFAULT_MANUAL[key] || out[key] === 'none' || out[key] === 'normal') out[key] = value;
      });
    });
    return out;
  }

  function setClass(name, enabled) { ROOT.classList.toggle(name, !!enabled); }

  function applyState() {
    const e = effectiveState();
    const fontPx = Math.max(12, Math.min(28, baseRootFontSize * e.fontScale));
    ROOT.style.setProperty('--bb-a11y-root-font-size', `${fontPx}px`);
    setClass('bb-a11y-font-scale', Math.abs(e.fontScale - 1) > 0.01);
    setClass('bb-a11y-readable-font', e.readableFont);
    setClass('bb-a11y-line-spacing', e.lineSpacing > 0);
    ROOT.style.setProperty('--bb-a11y-line-height', e.lineSpacing > 0 ? String(e.lineSpacing) : 'normal');
    setClass('bb-a11y-letter-spacing', e.letterSpacing > 0);
    ROOT.style.setProperty('--bb-a11y-letter-spacing', `${e.letterSpacing}em`);
    ['left','center','right'].forEach(a => setClass(`bb-a11y-align-${a}`, e.alignment === a));
    setClass('bb-a11y-highlight-links', e.highlightLinks);
    setClass('bb-a11y-focus-highlight', e.focusHighlight);
    setClass('bb-a11y-large-targets', e.largeTargets);
    setClass('bb-a11y-keyboard-nav', e.keyboardNav);
    setClass('bb-a11y-hide-images', e.hideImages);
    setClass('bb-a11y-stop-animations', e.stopAnimations);
    setClass('bb-a11y-large-cursor', e.largeCursor);
    setClass('bb-a11y-simplify', e.simplify);

    const filters = [];
    if (e.contrast === 'high') filters.push('contrast(1.45)');
    if (e.contrast === 'low') filters.push('contrast(.78)');
    if (e.saturation !== 1) filters.push(`saturate(${e.saturation})`);
    if (e.brightness !== 1) filters.push(`brightness(${e.brightness})`);
    if (e.grayscale) filters.push('grayscale(1)');
    if (e.invertColors || e.contrast === 'dark') filters.push('invert(.92)', 'hue-rotate(180deg)');
    if (e.colorVision === 'protanopia') filters.push('sepia(.2)', 'hue-rotate(-12deg)');
    if (e.colorVision === 'deuteranopia') filters.push('sepia(.12)', 'hue-rotate(8deg)', 'saturate(1.15)');
    if (e.colorVision === 'tritanopia') filters.push('sepia(.12)', 'hue-rotate(35deg)', 'saturate(1.1)');
    ROOT.style.setProperty('--bb-a11y-filter', filters.join(' ') || 'none');
    setClass('bb-a11y-filter', filters.length > 0);
    setClass('bb-a11y-dark', e.invertColors || e.contrast === 'dark');

    updateReadingGuide(e.readingGuide);
    updateReadingMask(e.readingMask);
    updateMagnifier(e.textMagnifier);
    enhanceKeyboardTargets(e.keyboardNav);
    saveState();
  }

  function enhanceKeyboardTargets(enabled) {
    document.querySelectorAll('[role="button"], [role="link"]').forEach(el => {
      if (el.closest(`#${HOST_ID}`)) return;
      if (enabled && !el.hasAttribute('tabindex')) {
        el.dataset.bbAddedTabindex = 'true';
        el.tabIndex = 0;
      } else if (!enabled && el.dataset.bbAddedTabindex === 'true') {
        el.removeAttribute('tabindex');
        delete el.dataset.bbAddedTabindex;
      }
    });
  }

  function ensureGuide() {
    let guide = document.getElementById('bb-a11y-reading-guide');
    if (!guide) {
      guide = document.createElement('div');
      guide.id = 'bb-a11y-reading-guide';
      guide.hidden = true;
      document.body.appendChild(guide);
    }
    return guide;
  }

  function updateReadingGuide(enabled) {
    const guide = ensureGuide();
    guide.hidden = !enabled;
  }

  function ensureMask() {
    let top = document.getElementById('bb-a11y-mask-top');
    let bottom = document.getElementById('bb-a11y-mask-bottom');
    if (!top) { top = document.createElement('div'); top.id = 'bb-a11y-mask-top'; top.hidden = true; document.body.appendChild(top); }
    if (!bottom) { bottom = document.createElement('div'); bottom.id = 'bb-a11y-mask-bottom'; bottom.hidden = true; document.body.appendChild(bottom); }
    return { top, bottom };
  }

  function positionReadingAids(y) {
    const e = effectiveState();
    const yy = Math.max(30, Math.min(window.innerHeight - 30, y));
    const guide = document.getElementById('bb-a11y-reading-guide');
    if (guide && e.readingGuide) guide.style.top = `${yy}px`;
    if (e.readingMask) {
      const {top,bottom} = ensureMask();
      const band = 54;
      top.style.height = `${Math.max(0, yy - band)}px`;
      bottom.style.height = `${Math.max(0, window.innerHeight - yy - band)}px`;
    }
  }

  function updateReadingMask(enabled) {
    const {top,bottom} = ensureMask();
    top.hidden = bottom.hidden = !enabled;
    if (enabled) positionReadingAids(window.innerHeight / 2);
  }

  function ensureMagnifier() {
    let el = document.getElementById('bb-a11y-magnifier');
    if (!el) {
      el = document.createElement('div');
      el.id = 'bb-a11y-magnifier';
      el.setAttribute('role','status');
      el.setAttribute('aria-live','polite');
      el.hidden = true;
      document.body.appendChild(el);
    }
    return el;
  }

  function updateMagnifier(enabled) {
    const el = ensureMagnifier();
    if (!enabled) { el.hidden = true; el.textContent = ''; }
  }

  function handlePointerMove(ev) {
    const e = effectiveState();
    if (e.readingGuide || e.readingMask) positionReadingAids(ev.clientY);
    if (e.textMagnifier) {
      const target = ev.target?.closest?.('p,li,a,button,label,h1,h2,h3,h4,h5,h6,span,td,th');
      const mag = ensureMagnifier();
      if (target && !target.closest(`#${HOST_ID}`)) {
        const text = (target.innerText || target.textContent || '').trim().replace(/\s+/g,' ');
        if (text) { mag.textContent = text.slice(0, 260); mag.hidden = false; return; }
      }
      mag.hidden = true;
    }
  }

  function buildShadowCss() {
    return `
      :host { all: initial; display:block !important; visibility:visible !important; opacity:1 !important; position:static !important; width:0 !important; height:0 !important; overflow:visible !important; --accent: ${escapeHtml(config.accent)}; font-family: Arial, Helvetica, sans-serif; }
      [hidden] { display:none !important; }
      *, *::before, *::after { box-sizing: border-box; }
      .launcher { position: fixed; bottom: 20px; z-index: 2147483647; border: 0; border-radius: 999px; background: var(--accent); color: #fff; min-height: 48px; padding: 0 18px; font: 700 15px/1 Arial,sans-serif; box-shadow: 0 4px 18px rgba(0,0,0,.24); cursor: pointer; display:flex; gap:8px; align-items:center; }
      .launcher.left { left: 20px; } .launcher.right { right: 20px; }
      .launcher:focus-visible, button:focus-visible, select:focus-visible, a:focus-visible { outline: 4px solid #ffbf47; outline-offset: 3px; }
      .backdrop { position: fixed; inset:0; background: rgba(0,0,0,.32); z-index:2147483645; }
      .panel { position: fixed; bottom: 18px; z-index:2147483646; width:min(430px, calc(100vw - 24px)); max-height:calc(100vh - 36px); overflow:auto; resize:both; background:#fff; color:#111; border:1px solid #d7d7d7; border-radius:18px; box-shadow:0 20px 60px rgba(0,0,0,.32); }
      .panel.left { left:12px; } .panel.right { right:12px; }
      .panel.small { width:min(350px, calc(100vw - 24px)); } .panel.large { width:min(560px, calc(100vw - 24px)); }
      .header { position:sticky; top:0; z-index:2; background:#fff; display:flex; align-items:center; justify-content:space-between; gap:10px; padding:16px; border-bottom:1px solid #e7e7e7; border-radius:18px 18px 0 0; }
      .title { font:800 21px/1.2 Arial,sans-serif; margin:0; }
      .header-actions { display:flex; gap:6px; }
      .icon-btn { width:40px; height:40px; border:1px solid #d2d2d2; background:#fff; color:#111; border-radius:10px; font-size:20px; cursor:pointer; }
      .body { padding:14px; }
      .topbar { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:14px; }
      label { display:block; font:700 12px/1.2 Arial,sans-serif; color:#444; margin-bottom:5px; }
      select { width:100%; height:42px; border:1px solid #c7c7c7; border-radius:9px; background:#fff; color:#111; padding:0 10px; font:600 14px Arial,sans-serif; }
      .section { border-top:1px solid #e8e8e8; padding-top:15px; margin-top:15px; }
      .section:first-of-type { border-top:0; margin-top:0; }
      h3 { margin:0 0 10px; font:800 15px/1.3 Arial,sans-serif; color:#222; }
      .grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px; }
      .grid.three { grid-template-columns:repeat(3,minmax(0,1fr)); }
      .control { min-height:48px; border:1px solid #d4d4d4; border-radius:11px; padding:9px 10px; background:#fff; color:#111; font:700 13px/1.2 Arial,sans-serif; cursor:pointer; text-align:left; }
      .control:hover { border-color:#888; } .control[aria-pressed="true"] { background:#111; color:#fff; border-color:#111; }
      .control .sub { display:block; font-size:11px; font-weight:500; opacity:.75; margin-top:3px; }
      .stepper { display:grid; grid-template-columns:1fr 44px 44px; gap:7px; align-items:center; margin:8px 0; }
      .stepper .value { display:block; margin-top:2px; font-size:12px; color:#666; }
      .stepper button { height:42px; border:1px solid #ccc; border-radius:9px; background:#fff; font-size:22px; cursor:pointer; }
      .full { grid-column:1/-1; }
      .footer { border-top:1px solid #e7e7e7; padding:14px 16px; display:flex; flex-wrap:wrap; gap:10px 16px; }
      .footer a { color:#111; text-decoration:underline; font:700 12px/1.4 Arial,sans-serif; }
      .reset { color:#9b1c1c; }
      .view { position:fixed; inset:20px; z-index:2147483647; background:#fff; color:#111; border-radius:16px; box-shadow:0 20px 70px rgba(0,0,0,.4); overflow:auto; padding:24px; }
      .view-head { display:flex; justify-content:space-between; align-items:center; gap:12px; border-bottom:1px solid #ddd; padding-bottom:12px; margin-bottom:18px; }
      .view h2 { margin:0; font:800 24px Arial,sans-serif; }
      .reader-content { max-width:780px; margin:0 auto; font:18px/1.75 Georgia,serif; }
      .reader-content h1,.reader-content h2,.reader-content h3 { line-height:1.3; }
      .outline { list-style:none; margin:0; padding:0; max-width:800px; }
      .outline li { margin:6px 0; }
      .outline button { width:100%; text-align:left; padding:10px 12px; background:#fff; border:1px solid #ddd; border-radius:9px; cursor:pointer; }
      .indent-2 { padding-left:18px!important; } .indent-3 { padding-left:34px!important; } .indent-4 { padding-left:50px!important; }
      .notice { padding:12px; background:#f4f4f4; border-radius:10px; font:14px/1.5 Arial,sans-serif; }
      @media (max-width: 600px) { .panel { inset:auto 6px 6px 6px; width:auto!important; max-height:92vh; resize:none; } .grid.three{grid-template-columns:repeat(2,minmax(0,1fr));} .view{inset:6px;} }
      @media (prefers-reduced-motion: reduce) { * { scroll-behavior:auto!important; } }
    `;
  }

  function button(key, action, pressed, sub='') {
    return `<button type="button" class="control" data-action="${action}" aria-pressed="${pressed ? 'true':'false'}">${escapeHtml(t(key))}${sub ? `<span class="sub">${escapeHtml(sub)}</span>`:''}</button>`;
  }

  function renderPanel() {
    const e = effectiveState();
    const profilesHtml = config.showProfiles ? `
      <section class="section"><h3>${escapeHtml(t('profiles'))}</h3><div class="grid three">
        ${button('motor','profile:motor',state.profiles.motor)}${button('vision','profile:vision',state.profiles.vision)}${button('epilepsy','profile:epilepsy',state.profiles.epilepsy)}${button('cognitive','profile:cognitive',state.profiles.cognitive)}${button('adhd','profile:adhd',state.profiles.adhd)}${button('colorblind','profile:colorblind',state.profiles.colorblind)}
      </div></section>` : '';

    panel.innerHTML = `
      <div class="header">
        <h2 class="title" id="bb-panel-title">${escapeHtml(t('title'))}</h2>
        <div class="header-actions"><button class="icon-btn reset" data-action="reset" aria-label="${escapeHtml(t('reset'))}" title="${escapeHtml(t('reset'))}">↺</button><button class="icon-btn" data-action="close" aria-label="${escapeHtml(t('close'))}" title="${escapeHtml(t('close'))}">×</button></div>
      </div>
      <div class="body">
        <div class="topbar">
          <div><label for="bb-lang">${escapeHtml(t('language'))}</label><select id="bb-lang" data-action="language"><option value="en">English</option><option value="fr">Français</option><option value="de">Deutsch</option><option value="it">Italiano</option><option value="es">Español</option><option value="ja">日本語</option></select></div>
          <div><label for="bb-size">${escapeHtml(t('size'))}</label><select id="bb-size" data-action="panel-size"><option value="small">${escapeHtml(t('small'))}</option><option value="medium">${escapeHtml(t('medium'))}</option><option value="large">${escapeHtml(t('large'))}</option></select></div>
        </div>
        ${profilesHtml}
        <section class="section"><h3>${escapeHtml(t('visual'))}</h3>
          <div class="grid">
            ${button('highContrast','contrast:high',e.contrast==='high')}${button('lowContrast','contrast:low',e.contrast==='low')}${button('darkMode','contrast:dark',e.contrast==='dark')}${button('grayscale','toggle:grayscale',e.grayscale)}${button('invert','toggle:invertColors',e.invertColors)}${button('links','toggle:highlightLinks',e.highlightLinks)}${button('focus','toggle:focusHighlight',e.focusHighlight)}${button('largeCursor','toggle:largeCursor',e.largeCursor)}
          </div>
          <div class="stepper"><div><strong>${escapeHtml(t('saturationUp'))} / ${escapeHtml(t('saturationDown'))}</strong><span class="value">${Math.round(e.saturation*100)}%</span></div><button data-action="saturation:-" aria-label="${escapeHtml(t('saturationDown'))}">−</button><button data-action="saturation:+" aria-label="${escapeHtml(t('saturationUp'))}">+</button></div>
          <div class="stepper"><div><strong>${escapeHtml(t('brightnessUp'))} / ${escapeHtml(t('brightnessDown'))}</strong><span class="value">${Math.round(e.brightness*100)}%</span></div><button data-action="brightness:-" aria-label="${escapeHtml(t('brightnessDown'))}">−</button><button data-action="brightness:+" aria-label="${escapeHtml(t('brightnessUp'))}">+</button></div>
          <div><label for="bb-color-vision">${escapeHtml(t('colorVision'))}</label><select id="bb-color-vision" data-action="color-vision"><option value="none">${escapeHtml(t('normal'))}</option><option value="deuteranopia">Deuteranopia</option><option value="protanopia">Protanopia</option><option value="tritanopia">Tritanopia</option></select></div>
        </section>
        <section class="section"><h3>${escapeHtml(t('content'))}</h3>
          <div class="stepper"><div><strong>${escapeHtml(t('textSize'))}</strong><span class="value">${Math.round(e.fontScale*100)}%</span></div><button data-action="font:-" aria-label="${escapeHtml(t('smaller'))}">−</button><button data-action="font:+" aria-label="${escapeHtml(t('larger'))}">+</button></div>
          <div><label for="bb-align">${escapeHtml(t('alignment'))}</label><select id="bb-align" data-action="alignment"><option value="default">${escapeHtml(t('default'))}</option><option value="left">${escapeHtml(t('left'))}</option><option value="center">${escapeHtml(t('center'))}</option><option value="right">${escapeHtml(t('right'))}</option></select></div>
          <div class="stepper"><div><strong>${escapeHtml(t('lineSpacing'))}</strong><span class="value">${e.lineSpacing ? e.lineSpacing.toFixed(1)+'×' : t('normal')}</span></div><button data-action="line:-">−</button><button data-action="line:+">+</button></div>
          <div class="stepper"><div><strong>${escapeHtml(t('letterSpacing'))}</strong><span class="value">${e.letterSpacing ? Math.round(e.letterSpacing*100)+'%' : t('normal')}</span></div><button data-action="letter:-">−</button><button data-action="letter:+">+</button></div>
          <div class="grid">${button('readableFont','toggle:readableFont',e.readableFont)}${button('magnifier','toggle:textMagnifier',e.textMagnifier)}${button('targets','toggle:largeTargets',e.largeTargets)}${button('keyboard','toggle:keyboardNav',e.keyboardNav)}</div>
        </section>
        <section class="section"><h3>${escapeHtml(t('reading'))}</h3><div class="grid">
          ${button('hideImages','toggle:hideImages',e.hideImages)}${button('stopAnimations','toggle:stopAnimations',e.stopAnimations)}${button('readingGuide','toggle:readingGuide',e.readingGuide)}${button('readingMask','toggle:readingMask',e.readingMask)}${button('simplify','toggle:simplify',e.simplify)}${button('readerView','reader-view',false)}${button('pageStructure','page-structure',false)}${button('speakSelection','speak',false)}${button('stopSpeech','stop-speech',false)}
        </div></section>
      </div>
      ${(config.showStatement || config.supportEmail) ? `<div class="footer">${config.showStatement ? `<a href="${escapeHtml(config.statementUrl || '/pages/accessibility-statement')}" target="_self">${escapeHtml(t('statement'))}</a>`:''}${config.supportEmail ? `<a href="mailto:${escapeHtml(config.supportEmail)}">${escapeHtml(t('feedback'))}</a>`:''}</div>`:''}
    `;
    panel.querySelector('#bb-lang').value = state.language;
    panel.querySelector('#bb-size').value = state.panelSize;
    panel.querySelector('#bb-align').value = e.alignment;
    panel.querySelector('#bb-color-vision').value = e.colorVision;
    panel.className = `panel ${config.position} ${state.panelSize}`;
  }

  function openPanel(opener) {
    lastOpener = opener || document.activeElement;
    shadow.querySelector('.backdrop').hidden = false;
    panel.hidden = false;
    panel.style.setProperty('display', 'block', 'important');
    panel.style.setProperty('visibility', 'visible', 'important');
    panel.style.setProperty('opacity', '1', 'important');
    renderPanel();
    setTimeout(() => panel.querySelector('[data-action="close"]')?.focus(), 0);
  }

  function closePanel() {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    panel.style.removeProperty('display');
    panel.style.removeProperty('visibility');
    panel.style.removeProperty('opacity');
    shadow.querySelector('.backdrop').hidden = true;
    lastOpener?.focus?.();
  }

  function toggleManual(key) {
    state.manual[key] = !effectiveState()[key];
  }

  function clamp(v,min,max,step=0) {
    let x = Math.max(min, Math.min(max, v));
    if (step) x = Math.round(x/step)*step;
    return Number(x.toFixed(3));
  }

  function handleAction(action, target) {
    if (!action) return;
    if (action === 'close') return closePanel();
    if (action === 'reset') {
      window.speechSynthesis?.cancel?.();
      state = { language: state.language, panelSize: state.panelSize, profiles:{motor:false,vision:false,epilepsy:false,cognitive:false,adhd:false,colorblind:false}, manual:{...DEFAULT_MANUAL} };
      applyState(); renderPanel(); return;
    }
    if (action.startsWith('profile:')) { const p=action.split(':')[1]; state.profiles[p]=!state.profiles[p]; applyState(); renderPanel(); return; }
    if (action.startsWith('toggle:')) { toggleManual(action.split(':')[1]); applyState(); renderPanel(); return; }
    if (action.startsWith('contrast:')) { const v=action.split(':')[1]; state.manual.contrast = effectiveState().contrast===v ? 'normal' : v; applyState(); renderPanel(); return; }
    if (action === 'font:+') state.manual.fontScale = clamp(effectiveState().fontScale + .1, .8, 1.6, .1);
    if (action === 'font:-') state.manual.fontScale = clamp(effectiveState().fontScale - .1, .8, 1.6, .1);
    if (action === 'line:+') state.manual.lineSpacing = clamp((effectiveState().lineSpacing || 1.3) + .2, 1.3, 2.1, .1);
    if (action === 'line:-') { const n=clamp((effectiveState().lineSpacing || 1.3) - .2, 1.3, 2.1, .1); state.manual.lineSpacing = n<=1.3 ? 0 : n; }
    if (action === 'letter:+') state.manual.letterSpacing = clamp(effectiveState().letterSpacing + .02, 0, .12, .01);
    if (action === 'letter:-') state.manual.letterSpacing = clamp(effectiveState().letterSpacing - .02, 0, .12, .01);
    if (action === 'saturation:+') state.manual.saturation = clamp(effectiveState().saturation + .15, .5, 1.8, .05);
    if (action === 'saturation:-') state.manual.saturation = clamp(effectiveState().saturation - .15, .5, 1.8, .05);
    if (action === 'brightness:+') state.manual.brightness = clamp(effectiveState().brightness + .1, .6, 1.4, .05);
    if (action === 'brightness:-') state.manual.brightness = clamp(effectiveState().brightness - .1, .6, 1.4, .05);
    if (['font:+','font:-','line:+','line:-','letter:+','letter:-','saturation:+','saturation:-','brightness:+','brightness:-'].includes(action)) { applyState(); renderPanel(); return; }
    if (action === 'reader-view') return openReaderView();
    if (action === 'page-structure') return openPageStructure();
    if (action === 'speak') return speakSelection();
    if (action === 'stop-speech') { window.speechSynthesis?.cancel?.(); return; }
  }

  function speakSelection() {
    if (!('speechSynthesis' in window)) return;
    const selected = String(window.getSelection?.() || '').trim();
    const focused = document.activeElement && !document.activeElement.closest?.(`#${HOST_ID}`) ? (document.activeElement.innerText || document.activeElement.getAttribute?.('aria-label') || '') : '';
    const text = (selected || focused).trim();
    if (!text) { alert(t('noSelection')); return; }
    speechSynthesis.cancel();
    speech = new SpeechSynthesisUtterance(text.slice(0,12000));
    speech.lang = ({en:'en-US',fr:'fr-FR',de:'de-DE',it:'it-IT',es:'es-ES',ja:'ja-JP'})[state.language] || 'en-US';
    speechSynthesis.speak(speech);
  }

  function sanitizeClone(node) {
    const clone = node.cloneNode(true);
    clone.querySelectorAll('script,style,noscript,iframe,form,button,input,select,textarea,nav,aside,video,audio,canvas').forEach(el=>el.remove());
    clone.querySelectorAll('*').forEach(el => {
      [...el.attributes].forEach(attr => { if (/^on/i.test(attr.name) || ['style','class','id','data-section-id'].includes(attr.name)) el.removeAttribute(attr.name); });
    });
    return clone;
  }

  function openReaderView() {
    const source = document.querySelector('main, [role="main"], #MainContent, .main-content');
    const view = shadow.querySelector('.view');
    const content = source ? sanitizeClone(source).innerHTML : `<p class="notice">${escapeHtml(t('noContent'))}</p>`;
    view.innerHTML = `<div class="view-head"><h2>${escapeHtml(t('readerTitle'))}</h2><button class="icon-btn" data-action="close-view" aria-label="${escapeHtml(t('closeView'))}">×</button></div><article class="reader-content">${content}</article>`;
    view.hidden = false;
    view.querySelector('[data-action="close-view"]')?.focus();
  }

  function openPageStructure() {
    const nodes = [...document.querySelectorAll('h1,h2,h3,h4,[role="main"],[role="navigation"],[role="search"]')].filter(el => !el.closest(`#${HOST_ID}`) && el.getClientRects().length);
    const items = nodes.map((el,i) => {
      if (!el.id) el.id = `bb-a11y-outline-${i}`;
      const tag = el.tagName.toLowerCase();
      const level = /^h[1-4]$/.test(tag) ? Number(tag[1]) : 1;
      const label = (el.innerText || el.getAttribute('aria-label') || el.getAttribute('role') || tag).trim().replace(/\s+/g,' ').slice(0,140);
      return `<li><button class="indent-${Math.min(level,4)}" data-scroll-id="${escapeHtml(el.id)}">${escapeHtml(label)}</button></li>`;
    }).join('');
    const view = shadow.querySelector('.view');
    view.innerHTML = `<div class="view-head"><h2>${escapeHtml(t('outlineTitle'))}</h2><button class="icon-btn" data-action="close-view" aria-label="${escapeHtml(t('closeView'))}">×</button></div><ul class="outline">${items || `<li class="notice">${escapeHtml(t('noContent'))}</li>`}</ul>`;
    view.hidden = false;
    view.querySelector('[data-action="close-view"]')?.focus();
  }

  function closeView() { const view=shadow.querySelector('.view'); view.hidden=true; panel.querySelector('[data-action="close"]')?.focus(); }

  function createWidget() {
    host = document.createElement('div');
    host.id = HOST_ID;
    host.dataset.version = VERSION;
    // Some Shopify themes hide empty DIVs. A shadow host appears empty to light-DOM CSS,
    // so force the host to remain renderable even when the theme uses div:empty rules.
    host.style.setProperty('display', 'block', 'important');
    host.style.setProperty('visibility', 'visible', 'important');
    host.style.setProperty('opacity', '1', 'important');
    host.style.setProperty('position', 'static', 'important');
    host.style.setProperty('width', '0', 'important');
    host.style.setProperty('height', '0', 'important');
    host.style.setProperty('overflow', 'visible', 'important');
    document.body.appendChild(host);
    shadow = host.attachShadow({mode:'open'});
    shadow.innerHTML = `
      <style>${buildShadowCss()}</style>
      <button type="button" class="launcher ${escapeHtml(config.position)}" aria-label="${escapeHtml(config.buttonLabel)}"><span aria-hidden="true">◉</span><span>${escapeHtml(config.buttonLabel)}</span></button>
      <div class="backdrop" hidden></div>
      <section class="panel ${escapeHtml(config.position)}" role="dialog" aria-labelledby="bb-panel-title" hidden></section>
      <section class="view" role="dialog" aria-modal="true" hidden></section>
    `;
    panel = shadow.querySelector('.panel');
    const launcher = shadow.querySelector('.launcher');
    launcher.hidden = !['floating','both'].includes(config.triggerMode);
    launcher.addEventListener('click', () => openPanel(launcher));
    shadow.querySelector('.backdrop').addEventListener('click', closePanel);
    shadow.addEventListener('click', ev => {
      const actionEl = ev.target.closest?.('[data-action]');
      if (actionEl) { const action=actionEl.dataset.action; if(action==='close-view') closeView(); else handleAction(action, actionEl); }
      const scrollEl = ev.target.closest?.('[data-scroll-id]');
      if (scrollEl) { const id=scrollEl.dataset.scrollId; closeView(); closePanel(); document.getElementById(id)?.scrollIntoView({behavior:'smooth',block:'center'}); document.getElementById(id)?.focus?.({preventScroll:true}); }
    });
    shadow.addEventListener('change', ev => {
      const action = ev.target.dataset.action;
      if (action === 'language') { state.language = ev.target.value; saveState(); renderPanel(); }
      if (action === 'panel-size') { state.panelSize = ev.target.value; saveState(); renderPanel(); }
      if (action === 'alignment') { state.manual.alignment = ev.target.value; applyState(); renderPanel(); }
      if (action === 'color-vision') { state.manual.colorVision = ev.target.value; applyState(); renderPanel(); }
    });
  }

  function globalClick(ev) {
    const trigger = ev.target.closest?.('a, [data-bb-accessibility-open]');
    if (!trigger || trigger.closest(`#${HOST_ID}`)) return;

    const href = trigger.getAttribute('href') || '';
    let hash = '';
    try {
      hash = new URL(href, window.location.href).hash;
    } catch (_) {}

    const isAccessibilityLink =
      trigger.hasAttribute('data-bb-accessibility-open') ||
      hash === '#accessibility' ||
      hash === '#bb-accessibility';

    if (!isAccessibilityLink) return;

    ev.preventDefault();
    ev.stopPropagation();
    openPanel(trigger);
  }

  function openFromHash() {
    if (
      window.location.hash === '#accessibility' ||
      window.location.hash === '#bb-accessibility'
    ) {
      openPanel(document.activeElement);
    }
  }

  function globalKeydown(ev) {
    if (ev.key === 'Escape') {
      const view = shadow?.querySelector('.view');
      if (view && !view.hidden) { closeView(); return; }
      closePanel();
    }
    if ((ev.key === 'Enter' || ev.key === ' ') && effectiveState().keyboardNav) {
      const el = document.activeElement;
      if (el?.dataset?.bbAddedTabindex === 'true') { ev.preventDefault(); el.click(); }
    }
  }

  function init() {
    if (document.getElementById(HOST_ID)) return;
    config = getConfig();
    try { baseRootFontSize = parseFloat(getComputedStyle(ROOT).fontSize) || 16; } catch (_) {}
    state = loadState();
    createWidget();
    applyState();
    document.addEventListener('click', globalClick, true);
    window.addEventListener('hashchange', openFromHash);
    document.addEventListener('keydown', globalKeydown, true);
    document.addEventListener('pointermove', handlePointerMove, {passive:true});
    setTimeout(openFromHash, 100);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();
